import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { User } from "lucide-react";
import { BACKEND_URL } from "../lib/apiBase";

const API = `${BACKEND_URL}/api`;

/**
 * Inline autocomplete for selecting an Empfehlungsgeber (partner).
 * Same behavior as the one in Contact.jsx: 200ms debounced search,
 * click-outside-to-close, returns name + partner_id via onChange.
 */
export default function PartnerAutocomplete({ value, onChange, placeholder = "Name des Empfehlungsgebers" }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const ref = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!value || value.length < 2) {
      setItems([]);
      setOpen(false);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await axios.get(`${API}/partners/search`, { params: { q: value } });
        setItems(res.data || []);
        setOpen(true);
      } catch {
        setItems([]);
      }
    }, 200);
    return () => clearTimeout(debounceRef.current);
  }, [value]);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const select = (p) => {
    onChange(p.name, p.id);
    setOpen(false);
  };

  return (
    <div className="relative" ref={ref}>
      <input
        data-testid="partner-autocomplete-input"
        type="text"
        value={value}
        onChange={(e) => {
          onChange(e.target.value, null);
          if (e.target.value.length >= 2) setOpen(true);
        }}
        onFocus={() => value.length >= 2 && items.length > 0 && setOpen(true)}
        placeholder={placeholder}
        className="al-input"
        autoComplete="off"
      />
      {open && items.length > 0 && (
        <ul
          data-testid="partner-suggestions"
          className="absolute z-20 left-0 right-0 mt-1 bg-white border border-[var(--al-line)] rounded-xl shadow-lg max-h-60 overflow-y-auto"
        >
          {items.map((p) => (
            <li
              key={p.id}
              data-testid={`partner-suggestion-${p.id}`}
              onMouseDown={() => select(p)}
              className="px-4 py-2.5 text-sm cursor-pointer hover:bg-violet-50 transition-colors flex items-center gap-2"
            >
              <User className="h-3.5 w-3.5 text-slate-400" />
              <span className="font-medium text-slate-800">{p.name}</span>
              {p.slug && <span className="text-xs text-slate-400 ml-auto font-mono-tab">/{p.slug}</span>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
